import React, { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  FiArrowRight,
  FiBookOpen,
  FiPhone,
  FiMail,
  FiMapPin,
  FiStar,
  FiGlobe,
  FiUsers,
  FiTarget,
  FiHeart,
  FiMenu,
  FiX,
} from 'react-icons/fi'
import CustomButton from '../components/CustomButton'
import { SEO_KEYWORDS_EXTRA } from '../seo/extraKeywords'
import { baseApiURL } from '../baseUrl'

const LandingPage = () => {
  const navigate = useNavigate()
  const contactRef = useRef(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [leadForm, setLeadForm] = useState({
    name: '',
    phone: '',
    email: '',
    programInterest: 'B.Ed',
    message: '',
  })
  const [leadSubmitting, setLeadSubmitting] = useState(false)
  const [leadFeedback, setLeadFeedback] = useState({ type: '', text: '' })

  const handleLogin = () => navigate('/login')

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth' })
    setIsMenuOpen(false)
  }

  const submitLeadInquiry = async (e) => {
    e.preventDefault()
    setLeadFeedback({ type: '', text: '' })
    const api = baseApiURL()
    if (!api) {
      setLeadFeedback({
        type: 'err',
        text: 'Form is not configured (missing API URL).',
      })
      return
    }
    if (!leadForm.name.trim()) {
      setLeadFeedback({ type: 'err', text: 'Please enter your name.' })
      return
    }
    const digits = String(leadForm.phone).replace(/\D/g, '')
    if (digits.length < 10) {
      setLeadFeedback({ type: 'err', text: 'Please enter a valid 10-digit phone number.' })
      return
    }
    setLeadSubmitting(true)
    try {
      const res = await fetch(`${api}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: leadForm.name.trim(),
          phone: digits.slice(-10),
          email: leadForm.email.trim(),
          programInterest: leadForm.programInterest,
          message: leadForm.message.trim(),
          source: 'landing',
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setLeadFeedback({
          type: 'err',
          text: json.message || 'Something went wrong. Please try again or call us.',
        })
        return
      }
      setLeadFeedback({
        type: 'ok',
        text: json.message || 'Thank you — we will contact you soon.',
      })
      setLeadForm({
        name: '',
        phone: '',
        email: '',
        programInterest: 'B.Ed',
        message: '',
      })
    } catch {
      setLeadFeedback({
        type: 'err',
        text: 'Network error. Check your connection or call us directly.',
      })
    } finally {
      setLeadSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 scroll-smooth overflow-x-hidden max-w-[100vw]">
      <Helmet>
        <title>B.Ed &amp; BP.Ed Institute — Bhadrak, Odisha | Devbhumi</title>
        <meta
          name="description"
          content="B.Ed &amp; BP.Ed admission coaching in Bhadrak (Bonth Chowk). Counselling, books &amp; online classes for Balasore, Karanjia, Andapur, Chhenapadi, Bonth, Thakurmunda &amp; Odisha."
        />
        <meta
          name="keywords"
          content={`top B.Ed institute Bhadrak, good B.Ed coaching Odisha, leading BP.Ed counselling Odisha, reputed B.Ed admission help Bhadrak, no 1 bed institute near Bonth Chowk, B.Ed Sukruli, B.Ed admission Bonth, B.Ed coaching Bonth Bhadrak, B.Ed institute near Bonth Chowk, B.Ed admission Andapur, B.Ed coaching Andapur, B.Ed admission Chhenapadi, B.Ed coaching Chhenapadi, B.Ed admission Thakurmunda, B.Ed coaching Thakurmunda, best B.Ed institute Bhadrak, B.Ed college Bhadrak, B.Ed admission Bhadrak, B.Ed coaching Bhadrak, best B.Ed institute Balasore, B.Ed admission Balasore, best B.Ed institute Karanjia, B.Ed admission Karanjia, BP.Ed admission Odisha, best BP.Ed institute Odisha, B.P.Ed coaching Odisha, B.Ed admission Odisha, teacher training Odisha, B.Ed institute near me, ${SEO_KEYWORDS_EXTRA}`}
        />
        <link rel="canonical" href="https://devbhumielearning.in/" />
        <meta name="robots" content="index,follow,max-image-preview:large" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://devbhumielearning.in/" />
        <meta
          property="og:title"
          content="B.Ed &amp; BP.Ed Institute — Bhadrak, Odisha | Devbhumi"
        />
        <meta
          property="og:description"
          content="Admission coaching for B.Ed &amp; BP.Ed in Bhadrak — support for Balasore, Karanjia, Bonth, Andapur, Chhenapadi, Thakurmunda &amp; nearby Odisha areas."
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768674429/WhatsApp_Image_2026-01-17_at_12.29.39_PM_athcxf.jpg"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://devbhumielearning.in/" />
        <meta name="twitter:title" content="B.Ed &amp; BP.Ed Institute — Bhadrak, Odisha | Devbhumi" />
        <meta
          name="twitter:description"
          content="Counselling, books &amp; online coaching for B.Ed &amp; BP.Ed — Bhadrak office serving Balasore, Karanjia &amp; across Odisha."
        />
      </Helmet>

      {/* 1. Header/Navbar */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex justify-between items-center gap-2 min-w-0">
          {/* Logo */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="shrink-0 bg-white p-1 rounded-lg shadow-lg border border-gray-100">
              <img
                src="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768674429/WhatsApp_Image_2026-01-17_at_12.29.39_PM_athcxf.jpg"
                alt="Devbhumi Educational Dreamzone logo"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-md object-cover"
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-lg sm:text-xl font-black leading-none text-blue-800 tracking-tighter uppercase truncate">
                Devbhumi
              </span>
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.08em] sm:tracking-[0.1em] text-gray-500 uppercase leading-tight mt-0.5 line-clamp-2 sm:line-clamp-none">
                Educational Dreamzone
              </span>
            </div>
          </div>

          {/* Desktop Links */}
          <div className="hidden lg:flex gap-8 font-bold text-xs text-gray-600 uppercase tracking-widest">
            <a href="#home" className="hover:text-blue-700 transition">
              Home
            </a>
            <a href="#about" className="hover:text-blue-700 transition">
              About
            </a>
            <a href="#courses" className="hover:text-blue-700 transition">
              Programs
            </a>
            <a href="#faculty" className="hover:text-blue-700 transition">
              Faculty
            </a>
            <a href="#gallery" className="hover:text-blue-700 transition">
              Gallery
            </a>
            <a href="#areas-local" className="hover:text-blue-700 transition">
              Locations
            </a>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <CustomButton
              onClick={handleLogin}
              className="hidden sm:block bg-blue-700 hover:bg-blue-800 text-white px-5 py-2 rounded-lg font-bold shadow-md transition-all active:scale-95"
            >
              Portal Login
            </CustomButton>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-2xl text-blue-800 focus:outline-none"
            >
              {isMenuOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-b border-gray-100 absolute w-full left-0 z-50 shadow-xl animate-in slide-in-from-top duration-300">
            <div className="flex flex-col p-6 gap-4 font-bold text-gray-600 uppercase tracking-widest text-sm">
              <a
                href="#home"
                onClick={() => setIsMenuOpen(false)}
                className="hover:text-blue-700 py-3 border-b border-gray-50"
              >
                Home
              </a>
              <a
                href="#about"
                onClick={() => setIsMenuOpen(false)}
                className="hover:text-blue-700 py-3 border-b border-gray-50"
              >
                About Us
              </a>
              <a
                href="#courses"
                onClick={() => setIsMenuOpen(false)}
                className="hover:text-blue-700 py-3 border-b border-gray-50"
              >
                Our Programs
              </a>
              <a
                href="#faculty"
                onClick={() => setIsMenuOpen(false)}
                className="hover:text-blue-700 py-3 border-b border-gray-50"
              >
                Faculty
              </a>
              <a
                href="#gallery"
                onClick={() => setIsMenuOpen(false)}
                className="hover:text-blue-700 py-3 border-b border-gray-50"
              >
                Gallery
              </a>
              <a
                href="#areas-local"
                onClick={() => setIsMenuOpen(false)}
                className="hover:text-blue-700 py-3 border-b border-gray-50"
              >
                Locations
              </a>
              <div className="pt-2">
                <CustomButton
                  onClick={handleLogin}
                  className="w-full bg-blue-700 text-white py-4 rounded-xl shadow-lg"
                >
                  Portal Login
                </CustomButton>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* 2. Hero Section */}
      <section
        id="home"
        className="relative pt-10 sm:pt-12 md:pt-24 pb-20 sm:pb-32 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100 via-white to-white overflow-hidden"
      >
        <div className="container mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center gap-10 md:gap-16">
          <div className="w-full md:w-1/2 z-10 min-w-0">
            <div className="inline-flex flex-wrap items-center gap-2 px-3 sm:px-4 py-2 mb-6 sm:mb-8 text-[10px] sm:text-xs font-black tracking-[0.08em] sm:tracking-[0.1em] text-blue-700 uppercase bg-blue-100/50 rounded-full border border-blue-200 max-w-full">
              <FiGlobe className="animate-pulse shrink-0" />{' '}
              <span className="break-words">Odisha&apos;s Elite Teaching Academy</span>
            </div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-black leading-tight text-gray-900 mb-4 tracking-tight break-words">
              Best <strong className="text-blue-800">B.Ed</strong> &{' '}
              <strong className="text-blue-800">BP.Ed</strong> institute —{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-600">
                Bhadrak, Balasore, Karanjia
              </span>
            </h1>
            <p className="text-xl sm:text-2xl md:text-3xl font-black text-gray-800 mb-4 sm:mb-6 tracking-tight break-words">
              Shape your teaching career.
            </p>
            <p className="text-base sm:text-xl text-gray-600 mb-8 sm:mb-12 leading-relaxed max-w-xl break-words">
              Top admission counselling & coaching for{' '}
              <strong>
                Bhadrak, Bonth, Andapur, Chhenapadi, Thakurmunda, Sukruli, Balasore,
                Karanjia, Baripada
              </strong>{' '}
              &amp; all Odisha — office support + online classes.
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 w-full max-w-xl">
              <CustomButton
                onClick={handleLogin}
                className="w-full sm:w-auto text-center bg-blue-700 hover:bg-blue-800 text-white px-6 sm:px-10 md:px-12 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-black shadow-2xl transition-all"
              >
                Apply Now
              </CustomButton>
              <Link
                to="/appointment"
                className="w-full sm:w-auto text-center px-6 sm:px-10 md:px-12 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-black border-2 border-blue-700 text-blue-800 hover:bg-blue-50 transition-all shadow-sm"
              >
                Book Counselling
              </Link>
              <button
                type="button"
                onClick={scrollToContact}
                className="w-full sm:w-auto px-6 sm:px-10 md:px-12 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-black border-2 border-gray-200 hover:border-blue-700 transition-all shadow-sm"
              >
                Contact Us
              </button>
            </div>
          </div>
          <div className="w-full md:w-1/2 relative px-0 sm:px-4 min-w-0">
            <img
              src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&q=80&w=800"
              alt="B.Ed and BP.Ed admission counselling and coaching in Odisha"
              className="relative w-full max-w-full h-auto rounded-[2rem] sm:rounded-[3rem] shadow-2xl border-[6px] sm:border-[8px] md:border-[12px] border-white z-10"
              loading="eager"
              decoding="async"
              fetchpriority="high"
            />
          </div>
        </div>
      </section>

      {/* 3. About Us Section */}
      <section id="about" className="py-16 sm:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row gap-16 items-center">
            <div className="md:w-1/2 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-blue-600 h-40 md:h-48 rounded-3xl flex flex-col items-center justify-center text-white text-4xl font-black">
                  15+ <span className="text-sm font-normal">Years</span>
                </div>
                <img
                  src="https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&q=80&w=400"
                  className="rounded-3xl h-64 w-full object-cover shadow-lg"
                  alt="Study environment for B.Ed and BP.Ed coaching"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="space-y-4 pt-12">
                <img
                  src="https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&q=80&w=400"
                  className="rounded-3xl h-64 w-full object-cover shadow-lg"
                  alt="Classroom learning for teacher education programs"
                  loading="lazy"
                  decoding="async"
                />
                <div className="bg-gray-100 h-40 md:h-48 rounded-3xl flex flex-col items-center justify-center text-blue-800 p-4 text-center">
                  <FiHeart className="text-3xl mb-2" />
                  <span className="font-bold">Student Success</span>
                </div>
              </div>
            </div>
            <div className="w-full md:w-1/2 min-w-0">
              <h2 className="text-2xl sm:text-4xl font-black mb-6 break-words">
                Built on Trust, Dedicated to{' '}
                <span className="text-blue-700">Odisha</span>.
              </h2>
              <p className="text-gray-600 text-base sm:text-lg mb-8 leading-relaxed break-words">
                Devbhumi Educational Dreamzone is the most trusted consultancy
                for teacher education. We help you navigate the complex
                admission processes of top-tier universities.
              </p>
              <div className="space-y-6">
                <AboutPoint
                  icon={<FiTarget className="text-red-500" />}
                  title="Our Mission"
                  desc="To bridge the gap between aspiring teachers and quality universities."
                />
                <AboutPoint
                  icon={<FiUsers className="text-green-500" />}
                  title="Regional Support"
                  desc="Bhadrak office near Bonth Chowk — students from Bonth, Andapur, Chhenapadi, Thakurmunda, Baripada and nearby areas get document support and guided admission help."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regional SEO — visible copy + internal links for crawlers and users */}
      <section id="areas-local" className="py-14 sm:py-20 bg-slate-50 border-y border-slate-100">
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
          <h2 className="text-2xl sm:text-4xl font-black mb-4 text-gray-900 break-words">
            Teacher-education support across{' '}
            <span className="text-blue-700">Northern Odisha &amp; Mayurbhanj</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6">
            Devbhumi Educational Dreamzone combines a{' '}
            <strong>Bhadrak office near Bonth Chowk</strong> with WhatsApp counselling
            and structured <strong>B.Ed coaching</strong> and <strong>BP.Ed (B.P.Ed) guidance</strong>{' '}
            for aspirants searching for phrases like{' '}
            <em>top B.Ed institute in Bhadrak</em>,{' '}
            <em>good BP.Ed admission help in Odisha</em>, or{' '}
            <em>leading teacher-training counselling near Karanjia and Balasore</em>.
            We clarify eligibility, document checks, admission timelines and revision support
            so you spend less time chasing rumours and more time preparing.
          </p>
          <h3 className="text-lg sm:text-2xl font-black text-gray-900 mt-10 mb-3">
            Bhadrak, Bonth, Andapur &amp; nearby blocks
          </h3>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            If you live in Jagannathpur, <strong>Bonth</strong>, <strong>Andapur</strong>,{' '}
            <strong>Chhenapadi</strong>, or commute from surrounding villages and want a reputed
            counselling desk for Kashmir / Nagarjuna pattern B.Ed enquiries, you can drop in at
            the office or{' '}
            <Link className="text-blue-700 font-bold underline underline-offset-2" to="/appointment">
              book counselling online
            </Link>
            . Our team also distributes curated study aids and aligns online batches with Odisha-centric
            exam patterns parents often compare when they Google{' '}
            <em>best Bed institute bhadrak</em> versus{' '}
            <em>no 1 B.Ed consultancy near Bonth Chowk</em>.
          </p>
          <h3 className="text-lg sm:text-2xl font-black text-gray-900 mt-8 mb-3">
            Balasore, Karanjia, Baripada, Thakurmunda &amp; Sukruli
          </h3>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Students from{' '}
            <strong>Balasore district</strong>, <strong>Karanjia</strong>,{' '}
            <strong>Baripada</strong>,{' '}
            <strong>Thakurmunda</strong>,{' '}
            <strong>Sukruli</strong> and broader <strong>Mayurbhanj</strong> routinely ask for verified
            information on counselling fees, hostel-linked colleges (where applicable),
            BP.Ed physique tests and backlog documentation. You can browse our locality guides —
            choose the hub closest to your town — so search intent like{' '}
            <em>top good B.Ed coaching Karanjia</em> lands on clearer, mapped content from our site hub.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3 text-gray-700 font-semibold mb-8 list-none pl-0">
            <li>
              <Link
                className="inline-flex items-center gap-2 text-blue-700 hover:underline break-words"
                to="/b-ed-admission-odisha"
              >
                ⟵ B.Ed admission Odisha overview
              </Link>
            </li>
            <li>
              <Link
                className="inline-flex items-center gap-2 text-blue-700 hover:underline break-words"
                to="/b-ed-admission-bhadrak"
              >
                ⟶ B.Ed admission Bhadrak (office hub)
              </Link>
            </li>
            <li>
              <Link
                className="inline-flex items-center gap-2 text-blue-700 hover:underline break-words"
                to="/b-ed-admission-balasore"
              >
                ⟶ B.Ed counselling Balasore
              </Link>
            </li>
            <li>
              <Link
                className="inline-flex items-center gap-2 text-blue-700 hover:underline break-words"
                to="/b-ed-admission-karanjia"
              >
                ⟶ B.Ed &amp; teacher-training help Karanjia
              </Link>
            </li>
            <li>
              <Link
                className="inline-flex items-center gap-2 text-blue-700 hover:underline break-words"
                to="/b-ed-admission-baripada"
              >
                ⟶ B.Ed roadmap Baripada
              </Link>
            </li>
            <li>
              <Link
                className="inline-flex items-center gap-2 text-blue-700 hover:underline break-words"
                to="/b-ed-admission-mayurbhanj"
              >
                ⟶ B.Ed / BP.Ed cues for Mayurbhanj
              </Link>
            </li>
            <li>
              <Link
                className="inline-flex items-center gap-2 text-blue-700 hover:underline break-words"
                to="/b-p-ed-admission-odisha"
              >
                ⟶ BP.Ed (B.P.Ed) admission Odisha
              </Link>
            </li>
          </ul>
          <p className="text-sm text-gray-500 leading-relaxed">
            Official announcements change each academic season; cross-check critical dates on the Odisha Directorate
            of Higher Education website —{' '}
            <a
              className="text-blue-600 underline underline-offset-2 font-medium"
              href="https://dhe.odisha.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
            >
              dhe.odisha.gov.in
            </a>{' '}
            — alongside our counselling.
          </p>
        </div>
      </section>

      {/* 4. Programs Section */}
      <section id="courses" className="py-16 sm:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-4xl font-black mb-10 sm:mb-16 underline decoration-blue-500 underline-offset-8 decoration-4 break-words px-1">
            Our Programs
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            <CourseCard
              title="B.Ed Admission"
              univ="Kashmir & Nagarjuna University"
              icon={<FiBookOpen />}
              onCheck={scrollToContact}
            />
            <CourseCard
              title="B.P.Ed Admission"
              univ="Yogi Vemana & Others"
              icon={<FiStar />}
              onCheck={scrollToContact}
            />
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
            <Link
              to="/b-ed-admission-odisha"
              className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition"
            >
              <div className="text-xs font-black tracking-[0.2em] uppercase text-blue-700">
                SEO Page
              </div>
              <div className="mt-2 text-xl font-black">
                B.Ed Admission Odisha (Counselling + Coaching)
              </div>
              <div className="mt-2 text-gray-600">
                For students across Odisha including Bhadrak, Balasore and
                Baripada.
              </div>
            </Link>
            <Link
              to="/b-ed-admission-bhadrak"
              className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition"
            >
              <div className="text-xs font-black tracking-[0.2em] uppercase text-blue-700">
                Location
              </div>
              <div className="mt-2 text-xl font-black">
                B.Ed Admission Bhadrak (Office Support)
              </div>
              <div className="mt-2 text-gray-600">
                Visit our Bhadrak office for counselling, documents and books.
              </div>
            </Link>
            <Link
              to="/b-ed-admission-balasore"
              className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition"
            >
              <div className="text-xs font-black tracking-[0.2em] uppercase text-blue-700">
                Location
              </div>
              <div className="mt-2 text-xl font-black">
                B.Ed Admission Balasore (Online + Offline Support)
              </div>
              <div className="mt-2 text-gray-600">
                Admission + coaching support for Balasore students.
              </div>
            </Link>
            <Link
              to="/b-p-ed-admission-odisha"
              className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition"
            >
              <div className="text-xs font-black tracking-[0.2em] uppercase text-blue-700">
                SEO Page
              </div>
              <div className="mt-2 text-xl font-black">
                BP.Ed Admission Odisha (Counselling + Coaching)
              </div>
              <div className="mt-2 text-gray-600">
                Full guidance from enquiry to confirmation, plus online classes.
              </div>
            </Link>
          </div>

          <div className="mt-10 max-w-4xl mx-auto grid md:grid-cols-3 gap-4 text-left">
            <Link
              to="/b-ed-admission-baripada"
              className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition"
            >
              <div className="text-sm font-black">B.Ed Admission Baripada</div>
              <div className="text-gray-600 mt-1 text-sm">
                Counselling + coaching for Baripada students.
              </div>
            </Link>
            <Link
              to="/b-ed-admission-mayurbhanj"
              className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition"
            >
              <div className="text-sm font-black">B.Ed Admission Mayurbhanj</div>
              <div className="text-gray-600 mt-1 text-sm">
                Support for Mayurbhanj district (Baripada/Karanjia).
              </div>
            </Link>
            <Link
              to="/b-ed-admission-karanjia"
              className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition"
            >
              <div className="text-sm font-black">B.Ed Admission Karanjia</div>
              <div className="text-gray-600 mt-1 text-sm">
                Admission + coaching support for Karanjia.
              </div>
            </Link>
          </div>

          <div className="mt-6 max-w-4xl mx-auto grid md:grid-cols-3 gap-4 text-left">
            <Link
              to="/kashmir-bed-bped-admission-odisha"
              className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition"
            >
              <div className="text-sm font-black">Kashmir B.Ed/BP.Ed Guidance</div>
              <div className="text-gray-600 mt-1 text-sm">
                Guidance for Odisha students (Kashmir).
              </div>
            </Link>
            <Link
              to="/west-bengal-bed-bped-admission-odisha"
              className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition"
            >
              <div className="text-sm font-black">
                West Bengal B.Ed/BP.Ed Guidance
              </div>
              <div className="text-gray-600 mt-1 text-sm">
                Guidance for Odisha students (West Bengal).
              </div>
            </Link>
            <Link
              to="/andhra-bed-bped-admission-odisha"
              className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition"
            >
              <div className="text-sm font-black">Andhra B.Ed/BP.Ed Guidance</div>
              <div className="text-gray-600 mt-1 text-sm">
                Guidance for Odisha students (Andhra).
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. Faculty Section */}
      <section id="faculty" className="py-16 sm:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-4xl font-black mb-4 break-words">Expert Mentors</h2>
          <p className="text-gray-400 mb-16 uppercase tracking-[0.2em] font-bold text-xs">
            Certified Counselors & Academic Directors
          </p>

          {/* Primary Leadership Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 mb-12">
            <FacultyMember
              name="Aditya Satapathy"
              role="Academic Head(Founder)"
              img="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768668676/WhatsApp_Image_2026-01-17_at_10.15.42_PM-removebg-preview_ppdgaz.png"
            />
            <FacultyMember
              name="Akash Mohanty"
              role="Account Head"
              img="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768669474/WhatsApp_Image_2026-01-17_at_10.26.26_PM_hbsgpy.jpg"
            />
            <FacultyMember
              name="Bidyadhar Nayak"
              role="Admin Chief"
              img="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768671906/WhatsApp_Image_2026-01-17_at_10.25.44_PM_1_1_sfzhpf.jpg"
            />
            <FacultyMember
              name="Aurobinda Ranjit"
              role="Head of Student Affairs"
              img="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768672291/WhatsApp_Image_2026-01-17_at_11.15.32_PM-removebg-preview_bmd940.png"
            />
          </div>

          {/* Management & Teaching Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
            <FacultyMember
              name="Pravasini Behera"
              role="Senior Teacher"
              img="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768668790/WhatsApp_Image_2026-01-17_at_10.15.56_PM_octsme.jpg"
            />
            <FacultyMember
              name="Lipsa Barik"
              role="Senior Teacher"
              img="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768672903/WhatsApp_Image_2026-01-17_at_11.31.03_PM_v5blcu.jpg"
            />
            <FacultyMember
              name="Satyabrata Sethi"
              role="Marketing Manager"
              img="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768672457/WhatsApp_Image_2026-01-17_at_11.11.06_PM_jhvzqr.jpg"
            />
            {/* ADD YOUR NEW STUDENT RECORD HEAD HERE */}
          </div>
        </div>
      </section>

      {/* 6. Gallery Section */}
      <section id="gallery" className="py-16 sm:py-24 bg-slate-900 text-white overflow-x-hidden">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-4xl font-black mb-8 sm:mb-12 break-words">
            Our Journey In Pictures
          </h2>
          <div className="columns-1 sm:columns-2 md:columns-3 gap-4 space-y-4">
            <img
              src="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768673322/2024-07-28_lvb0cm.webp"
              className="rounded-2xl w-full"
              alt="Devbhumi Educational Dreamzone student event in Odisha"
              loading="lazy"
              decoding="async"
            />
            <img
              src="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768673320/2024-07-28_1_itjhie.webp"
              className="rounded-2xl w-full"
              alt="B.Ed coaching and guidance session at Devbhumi Dreamzone"
              loading="lazy"
              decoding="async"
            />
            <img
              src="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768673320/2025-09-27_pti8ur.webp"
              className="rounded-2xl w-full"
              alt="BP.Ed admission support and student activities"
              loading="lazy"
              decoding="async"
            />
            <img
              src="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768673322/2024-07-28_2_lytqes.webp"
              className="rounded-2xl w-full"
              alt="Devbhumi Educational Dreamzone campus moments and achievements"
              loading="lazy"
              decoding="async"
            />
            <img
              src="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768673318/2024-07-28_3_res2fa.webp"
              className="rounded-2xl w-full"
              alt="Student success celebration at Devbhumi Dreamzone"
              loading="lazy"
              decoding="async"
            />
            <img
              src="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768673323/WhatsApp_Image_2026-01-17_at_11.34.35_PM_b1jyv9.jpg"
              className="rounded-2xl w-full"
              alt="Devbhumi Dreamzone Bhadrak office and student support"
              loading="lazy"
              decoding="async"
            />
            <img
              src="https://res.cloudinary.com/dsdrlehkp/image/upload/v1768673323/WhatsApp_Image_2026-01-17_at_11.34.34_PM_rvd7vt.jpg"
              className="rounded-2xl w-full"
              alt="Admission counselling and coaching team at Devbhumi Dreamzone"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* 7. Contact Us Form */}
      <footer
        ref={contactRef}
        id="contact"
        className="bg-white text-gray-900 pt-16 sm:pt-24 border-t border-gray-100 overflow-x-hidden"
      >
        <div className="container mx-auto px-4 sm:px-6 pb-12 sm:pb-20">
          <div className="bg-blue-700 rounded-[2rem] md:rounded-[3rem] p-6 sm:p-8 md:p-16 text-white flex flex-col lg:flex-row gap-10 lg:gap-12 items-stretch lg:items-center min-w-0">
            <div className="w-full lg:w-1/2 min-w-0">
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-black mb-6 leading-tight text-center lg:text-left break-words">
                Let's Secure Your Admission.
              </h2>
              <div className="space-y-6 mt-10">
                <ContactLink
                  icon={<FiPhone />}
                  text="+91 8328830471, +91 8637236390"
                />
                <ContactLink
                  icon={<FiMail />}
                  text="devbhumieductionaldreamzone@gmail.com"
                />
                <ContactLink
                  icon={<FiMapPin />}
                  text="jagannathpur Near Bonth chowk opposite of satsang vihar, Bhadrak,Odisha,756100"
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2 min-w-0 bg-white rounded-3xl p-5 sm:p-6 md:p-10 text-gray-900 shadow-2xl">
              <form className="space-y-4" onSubmit={submitLeadInquiry}>
                <input
                  type="text"
                  placeholder="Full Name"
                  required
                  value={leadForm.name}
                  onChange={(e) =>
                    setLeadForm((f) => ({ ...f, name: e.target.value }))
                  }
                  className="w-full p-4 bg-gray-50 rounded-xl outline-none border border-transparent focus:border-blue-500"
                />
                <input
                  type="tel"
                  placeholder="Phone Number (10 digits)"
                  required
                  value={leadForm.phone}
                  onChange={(e) =>
                    setLeadForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  className="w-full p-4 bg-gray-50 rounded-xl outline-none border border-transparent focus:border-blue-500"
                />
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={leadForm.email}
                  onChange={(e) =>
                    setLeadForm((f) => ({ ...f, email: e.target.value }))
                  }
                  className="w-full p-4 bg-gray-50 rounded-xl outline-none border border-transparent focus:border-blue-500"
                />
                <select
                  className="w-full p-4 bg-gray-50 rounded-xl outline-none border border-transparent focus:border-blue-500"
                  value={leadForm.programInterest}
                  onChange={(e) =>
                    setLeadForm((f) => ({ ...f, programInterest: e.target.value }))
                  }
                >
                  <option value="B.Ed">B.Ed Admission</option>
                  <option value="B.P.Ed">B.P.Ed Admission</option>
                  <option value="Other">Other</option>
                </select>
                <textarea
                  placeholder="Message (optional)"
                  rows={3}
                  value={leadForm.message}
                  onChange={(e) =>
                    setLeadForm((f) => ({ ...f, message: e.target.value }))
                  }
                  className="w-full p-4 bg-gray-50 rounded-xl outline-none border border-transparent focus:border-blue-500 resize-y"
                />
                {leadFeedback.text && (
                  <p
                    className={
                      leadFeedback.type === 'ok'
                        ? 'text-sm text-green-700 font-semibold'
                        : 'text-sm text-red-600 font-semibold'
                    }
                  >
                    {leadFeedback.text}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={leadSubmitting}
                  className="w-full bg-blue-700 text-white font-black py-4 rounded-xl shadow-lg hover:bg-blue-800 transition-all disabled:opacity-60"
                >
                  {leadSubmitting ? 'Sending…' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
        <div className="bg-gray-950 text-gray-600 py-10 text-center text-[10px] font-bold tracking-widest uppercase">
          &copy; {new Date().getFullYear()} Devbhumi Educational Dreamzone.
        </div>
      </footer>

      {/* FLOATING MOBILE CALL BUTTON */}
      <a
        href="tel:+918328830471"
        className="fixed z-50 bg-green-500 text-white p-3.5 sm:p-4 rounded-full shadow-2xl md:hidden animate-bounce bottom-[max(1rem,env(safe-area-inset-bottom,0px))] right-[max(1rem,env(safe-area-inset-right,0px))] sm:bottom-6 sm:right-6"
      >
        <FiPhone className="text-2xl" />
      </a>
    </div>
  )
}

// Sub-components
const AboutPoint = ({ icon, title, desc }) => (
  <div className="flex gap-4 items-start">
    <div className="p-3 bg-white shadow-md rounded-xl border border-gray-50">
      {icon}
    </div>
    <div>
      <h4 className="font-black text-gray-800 tracking-tight">{title}</h4>
      <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
    </div>
  </div>
)

const CourseCard = ({ title, univ, icon, onCheck }) => (
  <div className="bg-white p-6 sm:p-10 md:p-16 rounded-[1.75rem] sm:rounded-[2.5rem] shadow-xl border border-gray-100 group hover:border-blue-500 transition-all min-w-0">
    <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 text-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-8 text-3xl group-hover:bg-blue-700 group-hover:text-white transition-all">
      {icon}
    </div>
    <h3 className="text-xl sm:text-3xl font-black mb-2 break-words">{title}</h3>
    <p className="text-blue-600 font-bold mb-8 uppercase tracking-widest text-[10px]">
      {univ}
    </p>
    <button
      onClick={onCheck}
      className="flex items-center gap-2 mx-auto text-gray-900 font-black hover:text-blue-700"
    >
      Check Eligibility <FiArrowRight />
    </button>
  </div>
)

const FacultyMember = ({ name, role, img }) => (
  <div className="group min-w-0 px-0.5">
    <img
      src={img}
      alt={name}
      className="w-24 h-24 md:w-32 md:h-32 mx-auto rounded-full object-cover mb-4 border-4 border-gray-50 group-hover:border-blue-200 transition-all grayscale group-hover:grayscale-0"
    />
    <h4 className="font-black text-gray-800 text-xs sm:text-sm md:text-base break-words">
      {name}
    </h4>
    <p className="text-blue-600 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide sm:tracking-widest break-words leading-tight">
      {role}
    </p>
  </div>
)

const ContactLink = ({ icon, text }) => (
  <div className="flex items-start gap-3 sm:gap-4 text-blue-50 font-bold justify-center lg:justify-start min-w-0">
    <div className="text-xl shrink-0 pt-0.5">{icon}</div>
    <span className="text-sm md:text-lg text-left break-words [overflow-wrap:anywhere] min-w-0">
      {text}
    </span>
  </div>
)

export default LandingPage
